import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UploadCloud, Download, FileText, Loader2, AlertTriangle, CheckCircle2, ExternalLink,
  ArrowRight, ArrowLeft, RotateCcw,
} from "lucide-react";
import ImportMapper from "@/components/ImportMapper";
import ImportProfilesPanel from "@/components/ImportProfilesPanel";

const SAMPLE = `patient_name,pickup_address,dropoff_address,pickup_datetime,mobility,trip_type,payer,notes
John Doe,123 Main St,Riverdale Clinic,2026-03-01T09:30,ambulatory,one_way,Medicaid,First visit
Jane Smith,44 Oak Ave,Downtown Dialysis,2026-03-01T10:15,wheelchair,round_trip,Insurance,Recurring`;

const tripTypeLabel = (t) => (t === "round_trip" ? "Round trip" : t === "multi_leg" ? "Multi-leg" : "One-way");

function ImportSteps({ step, onJump }) {
  const steps = [
    { n: 1, label: "File" },
    { n: 2, label: "Map columns" },
    { n: 3, label: "Preview and create" },
  ];
  return (
    <div className="flex items-center gap-2 mb-5" data-testid="import-steps">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <button
            type="button"
            onClick={() => s.n < step && onJump(s.n)}
            disabled={s.n >= step}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              s.n === step
                ? "bg-[#0A192F] text-white border-[#0A192F]"
                : s.n < step
                ? "bg-white text-[#0A192F] border-slate-300 hover:border-slate-400"
                : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
            }`}
          >
            {s.n}. {s.label}
          </button>
          {i < steps.length - 1 && <div className="w-6 h-px bg-slate-300" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function RideImport() {
  const { user } = useAuth();
  const [tab, setTab] = useState("csv");
  const [companies, setCompanies] = useState([]);
  const isAdmin = user?.role === "agency_admin";
  const isClient = user?.role === "client_user";

  // CSV import: 3-step mapping flow state
  const [csvStep, setCsvStep] = useState(1);
  const [csvCompanyId, setCsvCompanyId] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectData, setInspectData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [mapperState, setMapperState] = useState({ mapping: {}, options: {}, defaults: {}, valid: false, missing: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [checkedRows, setCheckedRows] = useState(() => new Set());
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const csvFileRef = useRef();

  // Avo PO (PDF) import state
  const [avoFiles, setAvoFiles] = useState([]);
  const [avoReading, setAvoReading] = useState(false);
  const [avoPos, setAvoPos] = useState(null);
  const [avoSkipped, setAvoSkipped] = useState([]);
  const [avoCompanyId, setAvoCompanyId] = useState("");
  const [avoCommitting, setAvoCommitting] = useState(false);
  const [avoCommitResult, setAvoCommitResult] = useState(null);
  const avoFileRef = useRef();

  // Go T&T driver confirmation (PDF) import state
  const [gottFiles, setGottFiles] = useState([]);
  const [gottReading, setGottReading] = useState(false);
  const [gottCons, setGottCons] = useState(null);
  const [gottSkipped, setGottSkipped] = useState([]);
  const [gottCompanyId, setGottCompanyId] = useState("");
  const [gottCommitting, setGottCommitting] = useState(false);
  const [gottCommitResult, setGottCommitResult] = useState(null);
  const gottFileRef = useRef();

  useEffect(() => {
    if (user?.role !== "client_user") api.get("/companies").then((r) => setCompanies(r.data));
  }, [user]);

  const loadProfiles = () =>
    api.get("/import/profiles").then((r) => setProfiles(Array.isArray(r.data) ? r.data : r.data?.rows || [])).catch(() => {});

  useEffect(() => {
    if (tab === "csv") loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const selectedProfile = useMemo(() => profiles.find((p) => p.id === selectedProfileId) || null, [profiles, selectedProfileId]);

  const mapperInitial = useMemo(() => {
    if (selectedProfile) {
      return { mapping: selectedProfile.mapping || {}, options: selectedProfile.options || {}, defaults: selectedProfile.defaults || {} };
    }
    return { mapping: inspectData?.suggested_mapping || {}, options: {}, defaults: {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfile, inspectData]);

  const mapperResetKey = `${inspectData?.filename || "none"}::${selectedProfileId || "new"}`;

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "nemt_rides_sample.csv";
    a.click();
  };

  const resetCsvFlow = () => {
    setCsvStep(1);
    setCsvFile(null);
    setCsvCompanyId("");
    setInspectData(null);
    setSelectedProfileId("");
    setMapperState({ mapping: {}, options: {}, defaults: {}, valid: false, missing: [] });
    setPreviewData(null);
    setCheckedRows(new Set());
    setCommitResult(null);
    if (csvFileRef.current) csvFileRef.current.value = "";
  };

  const onCsvFileChosen = async (f) => {
    if (!f) return;
    setCsvFile(f);
    setInspectData(null);
    setSelectedProfileId("");
    setPreviewData(null);
    setCommitResult(null);
    setInspecting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/csv/inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInspectData(data);
      if (data.matched_profile_id) {
        setSelectedProfileId(data.matched_profile_id);
        toast.success(`Matched saved profile: ${data.matched_profile_name}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCsvFile(null);
    } finally {
      setInspecting(false);
    }
  };

  const deleteProfile = async (p) => {
    if (!window.confirm(`Delete mapping profile "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/import/profiles/${p.id}`);
      toast.success("Mapping profile deleted");
      setProfiles((prev) => prev.filter((x) => x.id !== p.id));
      if (selectedProfileId === p.id) setSelectedProfileId("");
    } catch (e) {
      if (e.response?.status === 403) toast.error("Your role can't delete mapping profiles.");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleProfileSaved = (profile) => {
    setProfiles((prev) => {
      const exists = prev.some((p) => p.id === profile.id);
      return exists ? prev.map((p) => (p.id === profile.id ? profile : p)) : [...prev, profile];
    });
    setSelectedProfileId(profile.id);
  };

  const goToPreview = async () => {
    if (!csvFile) return;
    if (!mapperState.valid) {
      toast.error(`Map the required fields first: ${mapperState.missing.join(", ")}`);
      return;
    }
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("mapping", JSON.stringify(mapperState.mapping));
      fd.append("defaults", JSON.stringify(mapperState.defaults));
      fd.append("options", JSON.stringify(mapperState.options));
      if (csvCompanyId) fd.append("company_id", csvCompanyId);
      const { data } = await api.post("/import/csv/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewData(data);
      setCheckedRows(new Set((data.rows || []).filter((r) => r.ok && !r.duplicate).map((r) => r.row)));
      setCsvStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleRowChecked = (rowNum, checked) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowNum);
      else next.delete(rowNum);
      return next;
    });
  };

  const updatePreviewRideField = (rowNum, field, value) => {
    setPreviewData((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (r.row === rowNum ? { ...r, ride: { ...r.ride, [field]: value } } : r)),
    }));
  };

  const doCommit = async () => {
    const chosen = (previewData?.rows || []).filter((r) => checkedRows.has(r.row)).map((r) => r.ride);
    if (!chosen.length) return toast.error("Check at least one row first");
    setCommitting(true);
    try {
      const payload = {
        rides: chosen,
        profile_id: selectedProfileId || null,
        profile_name: selectedProfile?.name || null,
        company_id: csvCompanyId || null,
        skip_duplicates: true,
      };
      const { data } = await api.post("/import/csv/commit", payload);
      setCommitResult(data);
      toast.success(`Created ${data.created} rides`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCommitting(false);
    }
  };

  const onAvoFilesChosen = (files) => {
    const pdfFiles = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfFiles.length) setAvoFiles(pdfFiles);
  };

  const readAvoPos = async () => {
    if (!avoFiles.length) return toast.error("Choose at least one PO PDF first");
    setAvoReading(true);
    setAvoCommitResult(null);
    try {
      const fd = new FormData();
      avoFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/import/avo/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const pos = (data.pos || []).map((po) => ({
        ...po,
        checked: !(po.existing_ride_ids && po.existing_ride_ids.length > 0),
        legs: (po.legs || []).map((l) => ({ ...l, mobility: po.mobility })),
      }));
      setAvoPos(pos);
      setAvoSkipped(data.skipped || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAvoReading(false);
    }
  };

  const togglePo = (idx, checked) => {
    setAvoPos((prev) => prev.map((po, i) => (i === idx ? { ...po, checked } : po)));
  };

  const updateLeg = (poIdx, legIdx, field, value) => {
    setAvoPos((prev) =>
      prev.map((po, i) =>
        i !== poIdx ? po : { ...po, legs: po.legs.map((l, j) => (j !== legIdx ? l : { ...l, [field]: value })) }
      )
    );
  };

  const tripTypeFor = (po) => (po.trip_type === "round_trip" || po.trip_type === "multi_leg" ? "round_trip" : "one_way");
  const totalCheckedLegs = (avoPos || []).filter((po) => po.checked).reduce((sum, po) => sum + po.legs.length, 0);

  const commitAvo = async () => {
    const rides = [];
    (avoPos || []).forEach((po) => {
      if (!po.checked) return;
      const tripType = tripTypeFor(po);
      po.legs.forEach((leg) => {
        const noteParts = [
          `Avo PO ${po.po_number} leg ${leg.leg}`,
          po.vehicle_type || null,
          po.wait_time ? `wait ${po.wait_time}` : null,
        ].filter(Boolean);
        rides.push({
          po_number: po.po_number,
          leg: leg.leg,
          direction: leg.direction,
          patient_name: po.rider_name,
          phone: po.rider_phone || null,
          pickup_address: leg.pickup_address,
          dropoff_address: leg.dropoff_address,
          pickup_datetime: leg.pickup_datetime,
          appointment_time: leg.appointment_time || null,
          mobility: leg.mobility,
          trip_type: tripType,
          payer: "Avo",
          notes: noteParts.join(", "),
          billable_miles: leg.leg === 1 && po.miles != null ? po.miles : null,
          fare: null,
        });
      });
    });
    if (!rides.length) return toast.error("Check at least one PO first");
    setAvoCommitting(true);
    try {
      const payload = { rides, company_id: avoCompanyId || null };
      const { data } = await api.post("/import/avo/commit", payload);
      setAvoCommitResult(data);
      toast.success(`Created ${data.created} rides`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAvoCommitting(false);
    }
  };

  const onGottFilesChosen = (files) => {
    const pdfFiles = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfFiles.length) setGottFiles(pdfFiles);
  };

  const readGottCons = async () => {
    if (!gottFiles.length) return toast.error("Choose at least one confirmation PDF first");
    setGottReading(true);
    setGottCommitResult(null);
    try {
      const fd = new FormData();
      gottFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/import/gott/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const cons = (data.confirmations || []).map((con) => ({
        ...con,
        checked: !(con.existing_ride_ids && con.existing_ride_ids.length > 0),
        legs: (con.legs || []).map((l) => ({
          ...l,
          direction: l.direction === "outbound" ? "OUTBOUND" : "INBOUND",
          mobility: con.mobility,
          estimated: !l.pickup_time,
          pickup_datetime: l.pickup_time
            ? (con.appointment_date ? `${con.appointment_date}T${l.pickup_time}` : "")
            : (l.pickup_datetime_placeholder || ""),
        })),
      }));
      setGottCons(cons);
      setGottSkipped(data.skipped || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGottReading(false);
    }
  };

  const toggleCon = (idx, checked) => {
    setGottCons((prev) => prev.map((con, i) => (i === idx ? { ...con, checked } : con)));
  };

  const updateGottLeg = (conIdx, legIdx, field, value) => {
    setGottCons((prev) =>
      prev.map((con, i) =>
        i !== conIdx ? con : { ...con, legs: con.legs.map((l, j) => (j !== legIdx ? l : { ...l, [field]: value })) }
      )
    );
  };

  const totalCheckedGottLegs = (gottCons || []).filter((con) => con.checked).reduce((sum, con) => sum + con.legs.length, 0);

  const buildGottNotes = (con, leg) => {
    const parts = [];
    if (leg.special_instructions) parts.push(leg.special_instructions);
    if (con.wait_time_fee != null) parts.push(`Wait time fee $${Number(con.wait_time_fee).toFixed(2)}`);
    if (con.no_show_fee != null) parts.push(`No show fee $${Number(con.no_show_fee).toFixed(2)}`);
    if (con.total_miles != null) parts.push(`Total miles ${con.total_miles}`);
    parts.push("Report wait hours within 24h of service");
    parts.push(`Ramp: ${con.ramp || "n/a"}`);
    parts.push(`Steps: ${con.steps || "n/a"}`);
    parts.push(`Height/Weight: ${con.height_weight || "n/a"}`);
    return parts.join("; ");
  };

  const commitGott = async () => {
    const rides = [];
    (gottCons || []).forEach((con) => {
      if (!con.checked) return;
      con.legs.forEach((leg, idx) => {
        rides.push({
          referral: con.referral,
          leg: idx + 1,
          direction: leg.direction,
          patient_name: con.client_name,
          phone: con.client_phone || null,
          dropoff_phone: idx === 0 ? (con.facility_phone || null) : (con.client_phone || null),
          pickup_address: leg.pickup_address,
          dropoff_address: leg.dropoff_address,
          pickup_datetime: leg.pickup_datetime,
          appointment_time: con.appointment_time || null,
          mobility: leg.mobility,
          payer: "Go T&T",
          notes: buildGottNotes(con, leg),
          fare: idx === 0 ? con.flat_rate : null,
        });
      });
    });
    if (!rides.length) return toast.error("Check at least one confirmation first");
    setGottCommitting(true);
    try {
      const payload = { rides, company_id: gottCompanyId || null };
      const { data } = await api.post("/import/gott/commit", payload);
      setGottCommitResult(data);
      toast.success(`Created ${data.created} rides`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGottCommitting(false);
    }
  };

  return (
    <div data-testid="import-page">
      <PageHeader subtitle="Ingestion" title="Ride Import" />

      <div className="flex items-center gap-2 mb-6">
        <button
          type="button"
          onClick={() => setTab("csv")}
          className={`text-sm px-4 py-2 rounded-md border transition ${tab === "csv" ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
          data-testid="import-tab-csv"
        >
          CSV
        </button>
        <button
          type="button"
          onClick={() => setTab("avo")}
          className={`text-sm px-4 py-2 rounded-md border transition ${tab === "avo" ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
          data-testid="import-tab-avo"
        >
          Avo PO (PDF)
        </button>
        <button
          type="button"
          onClick={() => setTab("gott")}
          className={`text-sm px-4 py-2 rounded-md border transition ${tab === "gott" ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
          data-testid="import-tab-gott"
        >
          Go T&T (PDF)
        </button>
      </div>

      {tab === "csv" && (
        <div>
          <ImportSteps step={csvStep} onJump={setCsvStep} />

          {csvStep === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
                {!isClient && (
                  <div className="mb-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Assign to client company (optional)</label>
                    <select
                      value={csvCompanyId}
                      onChange={(e) => setCsvCompanyId(e.target.value)}
                      className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-white"
                      data-testid="import-company-select"
                    >
                      <option value="">None (agency-direct)</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 block mb-2">CSV file</label>
                  <div
                    onClick={() => csvFileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      onCsvFileChosen(e.dataTransfer.files?.[0] || null);
                    }}
                    className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-[#005E82] hover:bg-slate-50 transition"
                    data-testid="dropzone"
                  >
                    {inspecting ? (
                      <Loader2 size={28} className="mx-auto text-slate-400 animate-spin" strokeWidth={1.5} />
                    ) : (
                      <UploadCloud size={28} className="mx-auto text-slate-400" strokeWidth={1.5} />
                    )}
                    <div className="text-sm mt-2 text-slate-700 font-medium">
                      {inspecting ? "Reading file..." : csvFile ? csvFile.name : "Click to select a CSV file, or drop it here"}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Any column layout works. You will map columns to RideWise fields next.</div>
                    <input
                      ref={csvFileRef}
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) => onCsvFileChosen(e.target.files?.[0] || null)}
                      data-testid="file-input"
                    />
                  </div>
                </div>

                {inspectData && (
                  <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 flex flex-wrap gap-x-5 gap-y-1">
                    <div><span className="text-slate-400">File:</span> {inspectData.filename}</div>
                    <div><span className="text-slate-400">Delimiter:</span> <span className="font-mono">{inspectData.delimiter}</span></div>
                    <div><span className="text-slate-400">Rows:</span> {inspectData.row_count}</div>
                    <div><span className="text-slate-400">Columns:</span> {inspectData.headers?.length || 0}</div>
                  </div>
                )}

                {inspectData && (
                  <div className="mt-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Saved profiles</label>
                    <select
                      value={selectedProfileId}
                      onChange={(e) => setSelectedProfileId(e.target.value)}
                      className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-white"
                      data-testid="import-profile-select"
                    >
                      <option value="">New mapping</option>
                      {profiles.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}{p.builtin ? " (built-in)" : ""}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center justify-between mt-5">
                  <button type="button" onClick={downloadSample} className="text-xs text-[#005E82] hover:underline flex items-center gap-1" data-testid="download-sample">
                    <Download size={14} /> Download a sample in the RideWise standard layout
                  </button>
                  <button
                    type="button"
                    onClick={() => setCsvStep(2)}
                    disabled={!inspectData}
                    className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5"
                    data-testid="import-next-file"
                  >
                    Map columns <ArrowRight size={14} />
                  </button>
                </div>
              </div>

              <ImportProfilesPanel
                profiles={profiles}
                selectedProfileId={selectedProfileId}
                matchedProfileId={inspectData?.matched_profile_id || null}
                isAdmin={isAdmin}
                onSelect={setSelectedProfileId}
                onDelete={deleteProfile}
              />
            </div>
          )}

          {csvStep === 2 && inspectData && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <ImportMapper
                headers={inspectData.headers || []}
                sampleRows={inspectData.sample_rows || []}
                initial={mapperInitial}
                resetKey={mapperResetKey}
                companyId={csvCompanyId}
                selectedProfile={selectedProfile}
                onSaved={handleProfileSaved}
                onChange={setMapperState}
              />
              {!mapperState.valid && mapperState.missing.length > 0 && (
                <div className="mt-3 text-xs text-amber-700">Still needed: {mapperState.missing.join(", ")}</div>
              )}
              <div className="flex items-center justify-between mt-5">
                <button
                  type="button"
                  onClick={() => setCsvStep(1)}
                  className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1.5"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={goToPreview}
                  disabled={previewLoading || !mapperState.valid}
                  className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5"
                  data-testid="import-preview"
                >
                  {previewLoading && <Loader2 size={14} className="animate-spin" />}
                  {previewLoading ? "Checking rows..." : "Preview"}
                </button>
              </div>
            </div>
          )}

          {csvStep === 3 && previewData && (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">{previewData.ok_count} ok</span>
                    <span className="text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">{previewData.error_count} errors</span>
                    <span className="text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">{previewData.duplicate_count} duplicates</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCsvStep(2)}
                    className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1.5"
                  >
                    <ArrowLeft size={14} /> Back to mapping
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[900px]" data-testid="import-preview-table">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                      <tr>
                        <th className="px-2 py-2 text-left w-8"></th>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Rider</th>
                        <th className="px-2 py-2 text-left">Pickup</th>
                        <th className="px-2 py-2 text-left">Dropoff</th>
                        <th className="px-2 py-2 text-left">Pickup date/time</th>
                        <th className="px-2 py-2 text-left">Mobility</th>
                        <th className="px-2 py-2 text-left">Payer</th>
                        <th className="px-2 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.map((r) => {
                        const ride = r.ride || {};
                        const checked = checkedRows.has(r.row);
                        return (
                          <tr key={r.row} className="border-t border-slate-100" data-testid={`import-row-${r.row}`}>
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleRowChecked(r.row, e.target.checked)}
                                data-testid={`import-row-check-${r.row}`}
                              />
                            </td>
                            <td className="px-2 py-2 font-mono text-slate-500">{r.row}</td>
                            <td className="px-2 py-2">{ride.patient_name || "(no name)"}</td>
                            <td className="px-2 py-2 min-w-[160px]">
                              {!r.ok ? (
                                <input
                                  type="text"
                                  value={ride.pickup_address || ""}
                                  onChange={(e) => updatePreviewRideField(r.row, "pickup_address", e.target.value)}
                                  className="w-full text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              ) : (
                                ride.pickup_address || ""
                              )}
                            </td>
                            <td className="px-2 py-2 min-w-[160px]">
                              {!r.ok ? (
                                <input
                                  type="text"
                                  value={ride.dropoff_address || ""}
                                  onChange={(e) => updatePreviewRideField(r.row, "dropoff_address", e.target.value)}
                                  className="w-full text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              ) : (
                                ride.dropoff_address || ""
                              )}
                            </td>
                            <td className="px-2 py-2 min-w-[150px]">
                              {!r.ok ? (
                                <input
                                  type="text"
                                  value={ride.pickup_datetime || ""}
                                  onChange={(e) => updatePreviewRideField(r.row, "pickup_datetime", e.target.value)}
                                  placeholder="YYYY-MM-DDTHH:MM"
                                  className="w-full text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              ) : (
                                ride.pickup_datetime || ""
                              )}
                            </td>
                            <td className="px-2 py-2 capitalize">{ride.mobility || ""}</td>
                            <td className="px-2 py-2">{ride.payer || ""}</td>
                            <td className="px-2 py-2">
                              {r.duplicate && (
                                <span className="inline-block text-[10px] uppercase tracking-[0.06em] bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 mr-1">
                                  Already imported
                                </span>
                              )}
                              {r.ok && !(r.errors || []).length ? (
                                !r.duplicate && (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <CheckCircle2 size={12} /> OK
                                  </span>
                                )
                              ) : (
                                <ul className="text-rose-700 space-y-0.5">
                                  {(r.errors || []).map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                              )}
                              {r.warnings?.length > 0 && (
                                <ul className="text-amber-700 mt-0.5 space-y-0.5">
                                  {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-5">
                  <button
                    type="button"
                    onClick={resetCsvFlow}
                    className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} /> Start over
                  </button>
                  <button
                    type="button"
                    onClick={doCommit}
                    disabled={committing || checkedRows.size === 0}
                    className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5"
                    data-testid="import-commit"
                  >
                    {committing && <Loader2 size={14} className="animate-spin" />}
                    {committing ? "Creating..." : `Create ${checkedRows.size} ride${checkedRows.size === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>

              {commitResult && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 size={18} />
                    <div className="font-display font-semibold">Created {commitResult.created} rides</div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <Link to="/app/rides" className="inline-flex items-center gap-1 text-sm text-[#005E82] hover:underline" data-testid="import-open-rides">
                      Open rides <ExternalLink size={13} />
                    </Link>
                    <button type="button" onClick={resetCsvFlow} className="text-sm text-slate-600 hover:underline">Start over</button>
                  </div>
                  {commitResult.skipped?.length > 0 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                      <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                        <AlertTriangle size={13} /> Skipped
                      </div>
                      <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                        {commitResult.skipped.map((s, i) => (
                          <li key={i}>{s.row != null ? `Row ${s.row}: ` : ""}{s.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "avo" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-2 text-slate-900 mb-1">
              <FileText size={18} className="text-[#005E82]" />
              <div className="font-display font-semibold">Upload Purchase Orders</div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Upload the Purchase Order PDFs Avo emailed you. We read the PO number, rider, appointment, addresses and legs, and you confirm before anything is created.
            </p>
            <div
              onClick={() => avoFileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onAvoFilesChosen(e.dataTransfer.files); }}
              className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-[#005E82] hover:bg-slate-50 transition"
            >
              <UploadCloud size={28} className="mx-auto text-slate-400" strokeWidth={1.5} />
              <div className="text-sm mt-2 text-slate-700 font-medium">
                {avoFiles.length ? `${avoFiles.length} PDF${avoFiles.length > 1 ? "s" : ""} selected` : "Click to select PO PDFs, or drop them here"}
              </div>
              <div className="text-xs text-slate-500 mt-1">PDF files only, multiple allowed</div>
              <input
                ref={avoFileRef}
                type="file"
                accept=".pdf"
                multiple
                hidden
                data-testid="avo-files"
                onChange={(e) => onAvoFilesChosen(e.target.files)}
              />
            </div>
            {avoFiles.length > 0 && (
              <ul className="mt-3 text-xs text-slate-600 space-y-0.5">
                {avoFiles.map((f, i) => <li key={`${f.name}-${i}`}>{f.name}</li>)}
              </ul>
            )}
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={readAvoPos}
                disabled={avoReading || avoFiles.length === 0}
                className="btn-primary disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="avo-preview"
              >
                {avoReading && <Loader2 size={14} className="animate-spin" />}
                {avoReading ? "Reading..." : "Read POs"}
              </button>
            </div>
            {avoSkipped.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle size={13} /> Skipped files
                </div>
                <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                  {avoSkipped.map((s, i) => <li key={i}>{s.filename}: {s.reason}</li>)}
                </ul>
              </div>
            )}
          </div>

          {avoPos !== null && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="font-display font-semibold text-slate-900">
                  {avoPos.length === 0 ? "No purchase orders found" : `${avoPos.length} purchase order${avoPos.length > 1 ? "s" : ""} found`}
                </div>
                {user?.role !== "client_user" && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Company (optional)</label>
                    <select
                      value={avoCompanyId}
                      onChange={(e) => setAvoCompanyId(e.target.value)}
                      className="text-sm px-3 py-2 rounded-md border border-slate-200 bg-white"
                      data-testid="avo-company-select"
                    >
                      <option value="">None (agency-direct)</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {avoPos.map((po, poIdx) => (
                  <div key={`${po.po_number}-${poIdx}`} className="border border-slate-200 rounded-md p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={po.checked}
                          onChange={(e) => togglePo(poIdx, e.target.checked)}
                          className="mt-1.5"
                          data-testid={`avo-po-${po.po_number}`}
                        />
                        <div>
                          <div className="font-mono text-sm font-semibold text-slate-900">{po.po_number}</div>
                          <div className="text-sm text-slate-700 mt-0.5">{po.rider_name}{po.rider_phone ? ` - ${po.rider_phone}` : ""}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {po.appointment_date || "Date TBD"} {po.appointment_time || ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] uppercase tracking-[0.08em] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                          {tripTypeLabel(po.trip_type)}
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.08em] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                          {po.vehicle_type || "Vehicle n/a"} - {po.mobility}
                        </span>
                        {po.miles != null && <span className="text-[11px] text-slate-500">{po.miles} mi</span>}
                        {po.total != null && <span className="text-[11px] text-slate-500 font-semibold">${Number(po.total).toFixed(2)}</span>}
                      </div>
                    </div>

                    {po.existing_ride_ids?.length > 0 && (
                      <div className="mt-2 text-xs text-rose-700 font-medium">
                        Rides for this PO already exist ({po.existing_ride_ids.length})
                      </div>
                    )}
                    {po.warnings?.length > 0 && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                        <ul className="text-xs text-amber-800 space-y-0.5">
                          {po.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                    )}

                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs min-w-[720px]">
                        <thead className="text-[10px] uppercase tracking-[0.08em] text-slate-500 bg-slate-50">
                          <tr>
                            <th className="text-left px-2 py-1.5">Leg</th>
                            <th className="text-left px-2 py-1.5">Direction</th>
                            <th className="text-left px-2 py-1.5">Pickup address</th>
                            <th className="text-left px-2 py-1.5">Dropoff address</th>
                            <th className="text-left px-2 py-1.5">Pickup date/time</th>
                            <th className="text-left px-2 py-1.5">Appt time</th>
                            <th className="text-left px-2 py-1.5">Mobility</th>
                          </tr>
                        </thead>
                        <tbody>
                          {po.legs.map((leg, legIdx) => (
                            <tr key={legIdx} className="border-t border-slate-100">
                              <td className="px-2 py-1.5 font-mono">{leg.leg}</td>
                              <td className="px-2 py-1.5">
                                <select
                                  value={leg.direction}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "direction", e.target.value)}
                                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                                >
                                  <option value="OUTBOUND">OUTBOUND</option>
                                  <option value="INBOUND">INBOUND</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={leg.pickup_address || ""}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "pickup_address", e.target.value)}
                                  className="w-full min-w-[160px] text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={leg.dropoff_address || ""}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "dropoff_address", e.target.value)}
                                  className="w-full min-w-[160px] text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="datetime-local"
                                  value={leg.pickup_datetime || ""}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "pickup_datetime", e.target.value)}
                                  title={leg.estimated ? "Estimated, confirm with the driver" : undefined}
                                  className={`text-xs px-2 py-1 rounded border ${leg.estimated ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={leg.appointment_time || ""}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "appointment_time", e.target.value)}
                                  placeholder="HH:MM"
                                  className="w-16 text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <select
                                  value={leg.mobility}
                                  onChange={(e) => updateLeg(poIdx, legIdx, "mobility", e.target.value)}
                                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                                >
                                  <option value="ambulatory">Ambulatory</option>
                                  <option value="wheelchair">Wheelchair</option>
                                  <option value="stretcher">Stretcher</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              {avoPos.length > 0 && (
                <div className="flex justify-end mt-5">
                  <button
                    type="button"
                    onClick={commitAvo}
                    disabled={avoCommitting || totalCheckedLegs === 0}
                    className="btn-primary disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="avo-commit"
                  >
                    {avoCommitting && <Loader2 size={14} className="animate-spin" />}
                    {avoCommitting ? "Creating..." : `Create ${totalCheckedLegs} rides`}
                  </button>
                </div>
              )}
            </div>
          )}

          {avoCommitResult && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18} />
                <div className="font-display font-semibold">Created {avoCommitResult.created} rides</div>
              </div>
              <Link to="/app/rides" className="mt-3 inline-flex items-center gap-1 text-sm text-[#005E82] hover:underline" data-testid="avo-open-rides">
                Open rides <ExternalLink size={13} />
              </Link>
              {avoCommitResult.skipped?.length > 0 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Skipped
                  </div>
                  <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                    {avoCommitResult.skipped.map((s, i) => (
                      <li key={i}>PO {s.po_number} leg {s.leg}: {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "gott" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-2 text-slate-900 mb-1">
              <FileText size={18} className="text-[#005E82]" />
              <div className="font-display font-semibold">Upload Driver Confirmations</div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Upload the Driver Confirmation PDFs Go T&amp;T emailed you. We read the referral number, client, appointment, addresses and both legs, and you confirm before anything is created.
            </p>
            <div
              onClick={() => gottFileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onGottFilesChosen(e.dataTransfer.files); }}
              className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-[#005E82] hover:bg-slate-50 transition"
            >
              <UploadCloud size={28} className="mx-auto text-slate-400" strokeWidth={1.5} />
              <div className="text-sm mt-2 text-slate-700 font-medium">
                {gottFiles.length ? `${gottFiles.length} PDF${gottFiles.length > 1 ? "s" : ""} selected` : "Click to select confirmation PDFs, or drop them here"}
              </div>
              <div className="text-xs text-slate-500 mt-1">PDF files only, multiple allowed</div>
              <input
                ref={gottFileRef}
                type="file"
                accept=".pdf"
                multiple
                hidden
                data-testid="gott-files"
                onChange={(e) => onGottFilesChosen(e.target.files)}
              />
            </div>
            {gottFiles.length > 0 && (
              <ul className="mt-3 text-xs text-slate-600 space-y-0.5">
                {gottFiles.map((f, i) => <li key={`${f.name}-${i}`}>{f.name}</li>)}
              </ul>
            )}
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={readGottCons}
                disabled={gottReading || gottFiles.length === 0}
                className="btn-primary disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="gott-preview"
              >
                {gottReading && <Loader2 size={14} className="animate-spin" />}
                {gottReading ? "Reading..." : "Read confirmations"}
              </button>
            </div>
            {gottSkipped.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle size={13} /> Skipped files
                </div>
                <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                  {gottSkipped.map((s, i) => <li key={i}>{s.filename}: {s.reason}</li>)}
                </ul>
              </div>
            )}
          </div>

          {gottCons !== null && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="font-display font-semibold text-slate-900">
                  {gottCons.length === 0 ? "No confirmations found" : `${gottCons.length} confirmation${gottCons.length > 1 ? "s" : ""} found`}
                </div>
                {user?.role !== "client_user" && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Company (optional)</label>
                    <select
                      value={gottCompanyId}
                      onChange={(e) => setGottCompanyId(e.target.value)}
                      className="text-sm px-3 py-2 rounded-md border border-slate-200 bg-white"
                      data-testid="gott-company-select"
                    >
                      <option value="">None (agency-direct)</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {gottCons.map((con, conIdx) => (
                  <div key={`${con.referral}-${conIdx}`} className="border border-slate-200 rounded-md p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={con.checked}
                          onChange={(e) => toggleCon(conIdx, e.target.checked)}
                          className="mt-1.5"
                          data-testid={`gott-con-${con.referral}`}
                        />
                        <div>
                          <div className="font-mono text-sm font-semibold text-slate-900">{con.referral}</div>
                          <div className="text-sm text-slate-700 mt-0.5">{con.client_name}{con.client_phone ? ` - ${con.client_phone}` : ""}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {con.appointment_date || "Date TBD"} {con.appointment_time || ""} - {con.facility_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] uppercase tracking-[0.08em] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                          Round trip
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.08em] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                          {con.transport_type || "Transport n/a"} - {con.mobility}
                        </span>
                        {con.total_miles != null && <span className="text-[11px] text-slate-500">{con.total_miles} mi</span>}
                        {con.flat_rate != null && <span className="text-[11px] text-slate-500 font-semibold">${Number(con.flat_rate).toFixed(2)}</span>}
                      </div>
                    </div>

                    {con.existing_ride_ids?.length > 0 && (
                      <div className="mt-2 text-xs text-rose-700 font-medium">
                        Rides for this referral already exist ({con.existing_ride_ids.length})
                      </div>
                    )}

                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs min-w-[720px]">
                        <thead className="text-[10px] uppercase tracking-[0.08em] text-slate-500 bg-slate-50">
                          <tr>
                            <th className="text-left px-2 py-1.5">Leg</th>
                            <th className="text-left px-2 py-1.5">Direction</th>
                            <th className="text-left px-2 py-1.5">Pickup address</th>
                            <th className="text-left px-2 py-1.5">Dropoff address</th>
                            <th className="text-left px-2 py-1.5">Pickup date/time</th>
                            <th className="text-left px-2 py-1.5">Mobility</th>
                          </tr>
                        </thead>
                        <tbody>
                          {con.legs.map((leg, legIdx) => (
                            <tr key={legIdx} className="border-t border-slate-100">
                              <td className="px-2 py-1.5 font-mono">{legIdx + 1}</td>
                              <td className="px-2 py-1.5">
                                <select
                                  value={leg.direction}
                                  onChange={(e) => updateGottLeg(conIdx, legIdx, "direction", e.target.value)}
                                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                                >
                                  <option value="OUTBOUND">OUTBOUND</option>
                                  <option value="INBOUND">INBOUND</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={leg.pickup_address || ""}
                                  onChange={(e) => updateGottLeg(conIdx, legIdx, "pickup_address", e.target.value)}
                                  className="w-full min-w-[160px] text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={leg.dropoff_address || ""}
                                  onChange={(e) => updateGottLeg(conIdx, legIdx, "dropoff_address", e.target.value)}
                                  className="w-full min-w-[160px] text-xs px-2 py-1 rounded border border-slate-200"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="datetime-local"
                                  value={leg.pickup_datetime || ""}
                                  onChange={(e) => updateGottLeg(conIdx, legIdx, "pickup_datetime", e.target.value)}
                                  title={leg.estimated ? "Wait time leg: placeholder time, confirm with the driver" : undefined}
                                  className={`text-xs px-2 py-1 rounded border ${leg.estimated ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <select
                                  value={leg.mobility}
                                  onChange={(e) => updateGottLeg(conIdx, legIdx, "mobility", e.target.value)}
                                  className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                                >
                                  <option value="ambulatory">Ambulatory</option>
                                  <option value="wheelchair">Wheelchair</option>
                                  <option value="stretcher">Stretcher</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {con.legs.some((l) => l.estimated) && (
                      <div className="mt-2 text-xs text-amber-700">
                        Return leg pickup time is a placeholder (appointment time plus 60 minutes). The driver waits on site; confirm the real time after the trip.
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {gottCons.length > 0 && (
                <div className="flex justify-end mt-5">
                  <button
                    type="button"
                    onClick={commitGott}
                    disabled={gottCommitting || totalCheckedGottLegs === 0}
                    className="btn-primary disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="gott-commit"
                  >
                    {gottCommitting && <Loader2 size={14} className="animate-spin" />}
                    {gottCommitting ? "Creating..." : `Create ${totalCheckedGottLegs} rides`}
                  </button>
                </div>
              )}
            </div>
          )}

          {gottCommitResult && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18} />
                <div className="font-display font-semibold">Created {gottCommitResult.created} rides</div>
              </div>
              <Link to="/app/rides" className="mt-3 inline-flex items-center gap-1 text-sm text-[#005E82] hover:underline" data-testid="gott-open-rides">
                Open rides <ExternalLink size={13} />
              </Link>
              {gottCommitResult.skipped?.length > 0 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Skipped
                  </div>
                  <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                    {gottCommitResult.skipped.map((s, i) => (
                      <li key={i}>Referral {s.referral} leg {s.leg}: {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
